import { useState } from "react";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  pointerWithin,
  type CollisionDetection,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { GripVertical } from "lucide-react";
import type { CatalogProduct, InactiveCatalogProduct } from "@school-sales-ai/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CategoryHeader } from "./CategoryHeader";
import { CurrencySelect } from "./CurrencySelect";
import {
  DeactivateConfirmDialog,
  OpenEnrollmentDialog,
  type DeactivateTarget,
} from "./DeactivateConfirmDialog";
import { InactiveProductsSection, type InactiveRow } from "./InactiveProductsSection";
import { InventorySection } from "./InventorySection";
import {
  ProductDescriptionDialog,
  ProductDescriptionIndicator,
} from "./ProductDescriptionDialog";
import { ProductGroupEditor } from "./ProductGroupEditor";
import { ProductMediaDialog } from "./ProductMediaDialog";
import { ProductMediaIndicator } from "./ProductMediaIndicator";
import { ProductRowMenu } from "./ProductRowMenu";
import { useCatalogDragSensors } from "./drag-sensors";
import { groupDropId, planProductDrop, type DropGroup } from "./product-drop";
import { CustomFieldsEditor } from "./CustomFieldsEditor";
import { CyclesEditor } from "./CyclesEditor";
import { cyclesTypeLabel, switchedSchedule } from "./row-fields";
import { Switch } from "@/components/ui/switch";
import type { CatalogEditor, CatalogList, EditorRow } from "./useCatalogEditor";
import { useIsDesktop } from "@/hooks/useIsDesktop";

export type CatalogFormMode = "maintenance" | "onboarding";

export type CatalogFormProps = {
  /**
   * `onboarding` embeds this as a wizard step: a first fill, so no row menu,
   * no media, no inactive list. Saving works the same in both — every edit
   * saves itself.
   */
  mode: CatalogFormMode;
  /** Owned by the page, which also needs it to flush before leaving. */
  editor: CatalogEditor;
  /**
   * Whether the client's plan grants `enrollment`.
   *
   * ON: the school shape — the enrollment-mode toggle, the cycles editor, and
   * each program's own calendar. OFF: products with a price, matching what the
   * agent reads under the same gate.
   *
   * Resolved by the PAGE (`useClientFeatures`), never here — the wizard renders
   * this before any client-scoped query is warm.
   */
  enrollmentEnabled?: boolean;
  /**
   * Whether this client's plan grants `orders`. Drives the stock column and the
   * tenant's own inventory switch — both of which live next to the products
   * because that is where an admin is already looking when they think about stock.
   */
  ordersEnabled?: boolean;
  /**
   * Whether this client's plan grants `quotes`: no prices and no currency on
   * screen, matching an agent that never sees them. Nothing stored is cleared.
   */
  quotesEnabled?: boolean;
  /**
   * Deactivated products, from `CatalogEntry.inactiveProducts`. Listed below the
   * groups for «Reactivar» (and «Eliminar» when nothing uses them). Omitted by
   * the onboarding wizard, which has none.
   */
  inactiveProducts?: InactiveCatalogProduct[];
};

type ProductRow = EditorRow<"products">;
type CategoryRow = EditorRow<"categories">;

/** Category cards share the products' drag context; this prefix tells them apart. */
const CATEGORY_DRAG_PREFIX = "cat:";

/**
 * Two kinds of thing are dragged in one context, and each may only land on its
 * own kind: a category among categories, a product among products and groups.
 *
 * For a product: a row when the pointer is over one; otherwise the group card
 * it is over; otherwise the nearest thing. Rows sit INSIDE their group's drop
 * area, and plain `closestCenter` would often pick the big card over the row
 * the admin is pointing at.
 */
const catalogCollision: CollisionDetection = (args) => {
  const draggingCategory = String(args.active.id).startsWith(CATEGORY_DRAG_PREFIX);
  const scoped = {
    ...args,
    droppableContainers: args.droppableContainers.filter(
      (c) => String(c.id).startsWith(CATEGORY_DRAG_PREFIX) === draggingCategory,
    ),
  };
  if (draggingCategory) return closestCenter(scoped);
  const hits = pointerWithin(scoped);
  const row = hits.find((hit) => !String(hit.id).startsWith("group:"));
  if (row) return [row];
  return hits.length > 0 ? hits : closestCenter(scoped);
};

/**
 * A category card, draggable by the grip in its header when `sortId` is set
 * (desktop, saved category). The card moves whole, products and all.
 */
function CategoryCard({
  sortId,
  label,
  children,
}: {
  sortId: string | undefined;
  label: string;
  children: (handle: React.ReactNode | undefined) => React.ReactNode;
}): React.JSX.Element {
  return sortId ? (
    <SortableCategoryCard sortId={sortId} label={label}>
      {children}
    </SortableCategoryCard>
  ) : (
    <>{children(undefined)}</>
  );
}

function SortableCategoryCard({
  sortId,
  label,
  children,
}: {
  sortId: string;
  label: string;
  children: (handle: React.ReactNode) => React.ReactNode;
}): React.JSX.Element {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: sortId });
  // dnd-kit positions the card by transform — the KanbanCard precedent for the
  // one inline style this codebase allows.
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, transition }
    : undefined;
  const handle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      aria-label={`Arrastrar la categoría ${label}`}
      className="-ml-2 shrink-0 cursor-grab touch-none rounded p-0.5 text-ink-300 hover:bg-ink-100 hover:text-ink-700 active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      <GripVertical size={14} />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? "relative z-10 opacity-60" : ""}>
      {children(handle)}
    </div>
  );
}

/** How programs by cycle relate to the cycles, stated once under their list. */
function IntakeStrip({ cycleKeys }: { cycleKeys: string[] }): React.JSX.Element {
  // `Product` has no relation to `Cycle`: a program by cycle is offered in EVERY
  // active cycle. Stated where the question comes up.
  return (
    <div className="flex flex-wrap items-baseline gap-2 border-t border-ink-050 bg-ink-050 px-4 py-2.5 text-[12.5px] text-ink-500">
      {cycleKeys.length === 0 ? (
        <span>
          Cada programa por ciclo se ofrece en todos los ciclos activos. Aún no hay ciclos
          configurados.
        </span>
      ) : (
        <>
          <span>
            Cada programa por ciclo se ofrece en{" "}
            {cycleKeys.length === 1 ? "el ciclo activo" : `los ${cycleKeys.length} ciclos activos`}:
          </span>
          {cycleKeys.map((key) => (
            <span
              key={key}
              className="rounded-full border border-ink-200 bg-white px-2 py-0.5 font-mono text-[11px] font-semibold text-ink-700"
            >
              {key}
            </span>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * The cycles card's title row, with the switch that replaced «Modalidad de
 * inscripción». Off is open enrollment: an academy where the student starts any
 * day sees no cycles to fill in, and its programs read «Inscripción abierta».
 */
function CyclesTitle({
  usesCycles,
  count,
  onChange,
}: {
  usesCycles: boolean;
  count: number;
  onChange: (usesCycles: boolean) => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 pb-3 pt-3.5">
      <h4 className="text-sm font-semibold text-ink-900">Ciclos escolares</h4>
      {usesCycles && (
        <span className="rounded-full bg-ink-050 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-ink-500">
          {count}
        </span>
      )}
      <label className="ml-auto flex items-center gap-2 text-[12.5px] text-ink-500">
        {usesCycles ? "Con ciclos" : "Sin ciclos"}
        <Switch
          checked={usesCycles}
          onCheckedChange={onChange}
          aria-label="Usar ciclos escolares"
        />
      </label>
    </div>
  );
}

/**
 * The catalog editor. Deliberately not the owner of its page: no `h-full`, no
 * internal mobile/desktop switcher — it is the Catálogo page and one step of
 * the onboarding wizard.
 *
 * Products live in GROUPS: the catalog's root, then each category in its
 * order. A category says what its products are (Primaria, Destilados); each
 * program's calendar is its own, so a group can mix both kinds. There is no save
 * button: every row saves itself (`useCatalogEditor`), and removing a saved row
 * always asks first — that confirmation is the undo that replaced «Descartar».
 */
export function CatalogForm({
  mode,
  editor,
  enrollmentEnabled = true,
  quotesEnabled = false,
  ordersEnabled = false,
  inactiveProducts = [],
}: CatalogFormProps): React.JSX.Element {
  // One media window and one description window for the whole form: each opens
  // from a row's icon and from its "⋯", so neither can live inside the row.
  const [mediaFor, setMediaFor] = useState<{ id: string; name: string } | null>(null);
  const [descriptionFor, setDescriptionFor] = useState<ProductRow | null>(null);
  const [deactivating, setDeactivating] = useState<DeactivateTarget | null>(null);
  const [confirmOpenEnrollment, setConfirmOpenEnrollment] = useState(false);
  const [retyping, setRetyping] = useState<ProductRow | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const isDesktop = useIsDesktop();
  const sensors = useCatalogDragSensors();

  if (!editor.isReady) {
    return (
      <div className="rounded-lg border border-ink-100 bg-white px-4 py-8 text-center text-[13px] text-ink-500">
        Cargando catálogo…
      </div>
    );
  }

  const maintenance = mode === "maintenance";
  const noun = enrollmentEnabled ? "programa" : "producto";
  const nounPlural = enrollmentEnabled ? "Programas" : "Productos";
  const { settings } = editor;
  const cyclesLabel = cyclesTypeLabel(settings.usesCycles);

  function requestRemove(list: CatalogList, row: EditorRow<CatalogList>, name: string): void {
    // A row never saved has nothing to deactivate and nothing to lose.
    if (row.id === null) {
      editor.discard(list, row.key);
      return;
    }
    setDeactivating({ list, key: row.key, id: row.id, name: name.trim() || "(sin nombre)" });
  }
  const removeProduct = (row: ProductRow): void => requestRemove("products", row, row.current.name);

  // ── Groups ────────────────────────────────────────────────────────────────
  // Products keep ONE global order; each group shows its slice of it. A reorder
  // inside a group sends the whole order, groups laid out as the screen shows
  // them — the root first, then each category.
  const products = editor.rows.products;
  const categories = editor.rows.categories;
  const savedCategoryIds = new Set(categories.flatMap((c) => (c.id ? [c.id] : [])));
  const rootRows = products.filter(
    (p) => p.current.categoryId === null || !savedCategoryIds.has(p.current.categoryId),
  );
  // Computed once per render: `reorderGroup` finds a group by identity.
  const categoryGroups: Array<{ category: CategoryRow; rows: ProductRow[] }> = categories.map(
    (category) => ({
      category,
      rows: category.id ? products.filter((p) => p.current.categoryId === category.id) : [],
    }),
  );

  const reorderGroup = (group: ProductRow[], reordered: ProductRow[]): void => {
    const groups = [rootRows, ...categoryGroups.map((g) => g.rows)].map((rows) =>
      rows === group ? reordered : rows,
    );
    editor.reorder(
      "products",
      groups.flat().map((r) => r.key),
    );
  };
  const reorderCategories = (index: number, delta: number): void => {
    const next = [...categories];
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(index + delta, 0, moved);
    editor.reorder(
      "categories",
      next.map((c) => c.key),
    );
  };

  // ── Row decorations (the catalog page only) ──────────────────────────────
  const openMedia = (row: ProductRow): void => {
    if (row.id) setMediaFor({ id: row.id, name: row.current.name });
  };
  const renderProductExtra = maintenance
    ? (row: ProductRow) => (
        <>
          <ProductMediaIndicator
            productId={row.id ?? undefined}
            productName={row.current.name}
            onOpen={() => openMedia(row)}
          />
          <ProductDescriptionIndicator
            productName={row.current.name}
            description={row.current.description}
            onOpen={() => setDescriptionFor(row)}
          />
        </>
      )
    : undefined;

  const rootLabel = categories.length === 0 ? nounPlural : "Sin categoría";
  const moveTargets = (row: ProductRow): Array<{ id: string | null; label: string }> =>
    [
      { id: null, label: "Sin categoría" },
      ...categories.flatMap((c) =>
        c.id ? [{ id: c.id, label: c.current.name.trim() || "(sin nombre)" }] : [],
      ),
    ].filter((target) => target.id !== row.current.categoryId);

  const scheduleAction = (row: ProductRow) => {
    if (!enrollmentEnabled) return undefined;
    const dated = row.current.schedule?.type === "specific_dates";
    const hasDates = dated && row.current.schedule?.startDate !== null;
    return {
      label: dated ? `Cambiar a ${cyclesLabel.toLowerCase()}` : "Cambiar a fechas propias",
      onSelect: () => {
        // Dropping real dates asks first; everything else switches at once.
        if (hasDates) {
          setRetyping(row);
          return;
        }
        editor.edit("products", row.key, { schedule: switchedSchedule(row.current) }, "now");
      },
    };
  };

  const renderRowMenu = maintenance
    ? (row: ProductRow) => (
        <ProductRowMenu
          productName={row.current.name}
          saved={row.id !== null}
          onOpenMedia={() => openMedia(row)}
          onOpenDescription={() => setDescriptionFor(row)}
          onRemove={() => removeProduct(row)}
          moveTargets={moveTargets(row)}
          onMove={(categoryId) => void editor.moveProduct(row.key, categoryId)}
          scheduleAction={scheduleAction(row)}
        />
      )
    : undefined;

  // A new row named like an INACTIVE product is refused by the server rather
  // than silently reactivated (it would come back with its old price, media and
  // description). The refusal carries its own way out.
  const inactiveByName = new Map(inactiveProducts.map((p) => [p.name.trim(), p]));
  const renderErrorAction = (row: EditorRow<CatalogList>) => {
    if (row.errorCode !== "INACTIVE_PRODUCT_EXISTS") return null;
    const match = "name" in row.current ? inactiveByName.get(row.current.name.trim()) : undefined;
    if (!match) return null;
    return (
      <>
        <button
          type="button"
          onClick={() => void editor.reactivateProduct(match.id, row.key)}
          className="font-semibold text-moss hover:underline"
        >
          Reactivar
        </button>
        <button
          type="button"
          onClick={() => editor.undo("products", row.key)}
          className="font-semibold text-ink-500 hover:underline"
        >
          Quitar este renglón
        </button>
      </>
    );
  };

  const groupProps = {
    editor,
    enrollmentEnabled,
    pricesHidden: quotesEnabled,
    usesCycles: settings.usesCycles,
    onRemove: removeProduct,
    renderProductExtra,
    renderRowMenu,
    renderErrorAction,
  };

  // The root is always there for a store (it is where a flat catalog lives). A
  // school that grouped everything does not need an empty "Sin categoría".
  const showRoot = !enrollmentEnabled || rootRows.length > 0 || categories.length === 0;

  // Drag and drop: the catalog page on a desktop only. On a phone the arrows
  // and «Mover a…» do the same job without fighting the scroll.
  const dragEnabled = maintenance && isDesktop;
  const dropGroups: DropGroup[] = [
    { categoryId: null, keys: rootRows.map((r) => r.key) },
    ...categoryGroups.flatMap(({ category, rows }) =>
      category.id ? [{ categoryId: category.id, keys: rows.map((r) => r.key) }] : [],
    ),
  ];
  const categorySortIds = categories.flatMap((c) =>
    c.id ? [`${CATEGORY_DRAG_PREFIX}${c.key}`] : [],
  );
  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    setDragging(null);
    if (!over) return;
    const activeId = String(active.id);
    if (activeId.startsWith(CATEGORY_DRAG_PREFIX)) {
      const from = categorySortIds.indexOf(activeId);
      const to = categorySortIds.indexOf(String(over.id));
      if (from < 0 || to < 0 || from === to) return;
      const keys = categories.map((c) => c.key);
      const fromKey = activeId.slice(CATEGORY_DRAG_PREFIX.length);
      const toKey = String(over.id).slice(CATEGORY_DRAG_PREFIX.length);
      const reordered = keys.filter((k) => k !== fromKey);
      reordered.splice(reordered.indexOf(toKey) + (from < to ? 1 : 0), 0, fromKey);
      editor.reorder("categories", reordered);
      return;
    }
    const plan = planProductDrop(dropGroups, String(active.id), String(over.id));
    if (!plan) return;
    if (plan.kind === "reorder") {
      editor.reorder("products", plan.orderedKeys);
      return;
    }
    void editor.moveProduct(plan.key, plan.categoryId, plan.orderedKeys);
  };
  const draggingName = dragging?.startsWith(CATEGORY_DRAG_PREFIX)
    ? categories.find((c) => `${CATEGORY_DRAG_PREFIX}${c.key}` === dragging)?.current.name
    : products.find((p) => p.key === dragging)?.current.name;

  const rootCard = (
    <>
      {showRoot && (
        <ProductGroupEditor
          {...groupProps}
          rows={rootRows}
          categoryId={null}
          dropGroupId={dragEnabled ? groupDropId(null) : undefined}
          title={rootLabel}
          emptyLabel={`Aún no hay ${noun}s${categories.length > 0 ? " sin categoría" : ""}.`}
          onReorder={(reordered) => reorderGroup(rootRows, reordered)}
        />
      )}
    </>
  );

  const categoryCards = categoryGroups.map(({ category, rows }, index) => (
    <CategoryCard
      key={category.key}
      sortId={dragEnabled && category.id ? `${CATEGORY_DRAG_PREFIX}${category.key}` : undefined}
      label={category.current.name.trim() || "(sin nombre)"}
    >
      {(handle) => (
        <ProductGroupEditor
          {...groupProps}
          rows={rows}
          categoryId={category.id}
          dropGroupId={dragEnabled && category.id ? groupDropId(category.id) : undefined}
          title={category.current.name.trim() || "Nueva categoría"}
          categoryName={category.saved?.name.trim() || undefined}
          header={
            <CategoryHeader
              editor={editor}
              row={category}
              productCount={rows.length}
              canMoveUp={Boolean(categories[index - 1]?.id)}
              canMoveDown={Boolean(categories[index + 1]?.id)}
              onMove={(delta) => reorderCategories(index, delta)}
              onRemove={() => requestRemove("categories", category, category.current.name)}
              dragMode={dragEnabled}
              dragHandle={handle}
            />
          }
          addDisabledReason={
            category.id ? undefined : `Escribe el nombre de la categoría para agregar ${noun}s.`
          }
          emptyLabel={`Aún no hay ${noun}s en esta categoría.`}
          onReorder={(reordered) => reorderGroup(rows, reordered)}
        />
      )}
    </CategoryCard>
  ));

  const groups = (
    <>
      {dragEnabled ? (
        <DndContext
          sensors={sensors}
          collisionDetection={catalogCollision}
          onDragStart={({ active }) => setDragging(String(active.id))}
          onDragCancel={() => setDragging(null)}
          onDragEnd={handleDragEnd}
        >
          {rootCard}
          <SortableContext items={categorySortIds} strategy={verticalListSortingStrategy}>
            {categoryCards}
          </SortableContext>
          <DragOverlay>
            {draggingName !== undefined && (
              <div className="rounded-md border border-ink-200 bg-white px-3 py-2 text-[13px] font-medium text-ink-900 shadow-lg">
                {draggingName || "(sin nombre)"}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        <>
          {rootCard}
          {categoryCards}
        </>
      )}
      <button
        type="button"
        onClick={() => editor.add("categories", { name: "" })}
        className="self-start rounded-lg border border-dashed border-ink-200 px-4 py-2.5 text-[12.5px] font-semibold text-moss hover:bg-ink-050"
      >
        + Agregar categoría
      </button>
    </>
  );

  // Inactive products are listed apart. One just reactivated stays in the
  // server's list until the next fetch, so the editor's rows are the tiebreak.
  const activeIds = new Set(products.flatMap((r) => (r.id ? [r.id] : [])));
  const inactiveRows: InactiveRow[] = maintenance
    ? inactiveProducts
        .filter((p) => !activeIds.has(p.id))
        .map(({ usage, ...product }) => ({ product, usage }))
    : [];
  const inactiveSection = (
    <InactiveProductsSection
      rows={inactiveRows}
      usesCycles={!enrollmentEnabled || settings.usesCycles}
      noun={nounPlural}
      onReactivate={(product: CatalogProduct & { id: string }) =>
        editor.reactivateProduct(product.id)
      }
    />
  );

  const inventory = (
    <InventorySection
      products={products.flatMap((r) => (r.id ? [{ ...r.current, id: r.id }] : []))}
    />
  );

  const dialogs = (
    <>
      {mediaFor && (
        <ProductMediaDialog
          productId={mediaFor.id}
          productName={mediaFor.name}
          onClose={() => setMediaFor(null)}
        />
      )}
      {descriptionFor && (
        <ProductDescriptionDialog
          productName={descriptionFor.current.name}
          noun={noun}
          description={descriptionFor.current.description}
          onSave={(description) => editor.saveDescription(descriptionFor.key, description)}
          onClose={() => setDescriptionFor(null)}
        />
      )}
      <DeactivateConfirmDialog
        target={deactivating}
        noun={noun}
        onConfirm={(target) => editor.deactivate(target.list, target.key)}
        onClose={() => setDeactivating(null)}
      />
      <AlertDialog open={retyping !== null} onOpenChange={(open: boolean) => !open && setRetyping(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Cambiar «{retyping?.current.name}» a {cyclesLabel.toLowerCase()}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se borran sus fechas ({retyping?.current.schedule?.startDate} al{" "}
              {retyping?.current.schedule?.endDate})
              {settings.usesCycles
                ? " y pasa a ofrecerse en todos los ciclos activos."
                : " y el agente dirá que se puede empezar en cualquier momento."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (retyping) {
                  editor.edit(
                    "products",
                    retyping.key,
                    { schedule: switchedSchedule(retyping.current) },
                    "now",
                  );
                }
                setRetyping(null);
              }}
            >
              Cambiar a {cyclesLabel.toLowerCase()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  const customFields = (
    <CustomFieldsEditor
      editor={editor}
      sortable={dragEnabled}
      onRemove={(row) => requestRemove("customFields", row, row.current.name)}
    />
  );

  // Without the capability there are no intake periods and no calendars.
  // `usesCycles` and the cycles are left EXACTLY as they are — never rewritten
  // from a screen that does not show them.
  if (!enrollmentEnabled) {
    return (
      <div className="flex flex-col gap-4">
        {!quotesEnabled && (
          <CurrencySelect
            value={settings.currency}
            onChange={(currency) => void editor.setCurrency(currency)}
          />
        )}
        {groups}
        {inactiveSection}
        {ordersEnabled && inventory}
        {customFields}
        {dialogs}
      </div>
    );
  }

  const savedCycles = editor.rows.cycles.flatMap((r) =>
    r.id ? [{ id: r.id, key: r.current.key, label: r.current.label }] : [],
  );
  const cycleKeys = editor.rows.cycles
    .map((r) => r.current.key.trim())
    .filter((key) => key.length > 0);
  const cyclesTitle = (
    <CyclesTitle
      usesCycles={settings.usesCycles}
      count={editor.rows.cycles.length}
      onChange={(usesCycles) => {
        // Turning cycles off deactivates every one: ask first when there are any.
        if (!usesCycles && savedCycles.length > 0) {
          setConfirmOpenEnrollment(true);
          return;
        }
        void editor.setUsesCycles(usesCycles);
      }}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {settings.usesCycles ? (
        <CyclesEditor
          editor={editor}
          sortable={dragEnabled}
          onRemove={(row) => requestRemove("cycles", row, row.current.key)}
          header={cyclesTitle}
          footer={<IntakeStrip cycleKeys={cycleKeys} />}
        />
      ) : (
        <section className="rounded-lg border border-ink-100 bg-white" aria-label="Ciclos escolares">
          {cyclesTitle}
          <p className="border-t border-ink-050 px-4 py-3 text-[12.5px] text-ink-500">
            Sin ciclos: el agente dirá que se puede empezar en cualquier momento.
          </p>
        </section>
      )}

      {/* No currency and no prices in the school (enrollment) shape: a school
          does not sell through its catalog. Stored values are never touched —
          nothing here sends them. */}
      {groups}
      {inactiveSection}
      {ordersEnabled && inventory}
      {customFields}
      {dialogs}

      <OpenEnrollmentDialog
        open={confirmOpenEnrollment}
        cycles={savedCycles}
        onConfirm={() => editor.setUsesCycles(false)}
        onClose={() => setConfirmOpenEnrollment(false)}
      />
    </div>
  );
}
